"use client";

import { useEffect, useRef, useState } from "react";
import Link from "next/link";

const CANVAS_W = 230;
const CANVAS_H = 34;

interface Blade {
  x: number;
  h: number;
  w: number;
  baseTilt: number;
  swaySpeed: number;
  swayAmp: number;
  phase: number;
  color: string;
  growSpeed: number;
  retractSpeed: number;
  delay: number;
  progress: number;
}

// Preset lush natural palette
const COLORS = [
  "#16a34a", // rich green
  "#22c55e", // vivid emerald
  "#15803d", // deep forest
  "#4ade80", // fresh spring green
  "#10b981", // bright mint/emerald
  "#86efac", // light sprout highlight
];

function createBlades(): Blade[] {
  const blades: Blade[] = [
    // Left cluster (framing left of "Digital Garden")
    { x: 18, h: 22, w: 2.2, baseTilt: -0.28, swaySpeed: 3.2, swayAmp: 0.12, phase: 0.2, color: COLORS[0], growSpeed: 7.0, retractSpeed: 8.5, delay: 0.08, progress: 0 },
    { x: 25, h: 27, w: 2.4, baseTilt: -0.18, swaySpeed: 2.8, swayAmp: 0.10, phase: 1.1, color: COLORS[1], growSpeed: 8.0, retractSpeed: 9.0, delay: 0.04, progress: 0 },
    { x: 32, h: 20, w: 2.0, baseTilt: -0.12, swaySpeed: 3.5, swayAmp: 0.14, phase: 2.3, color: COLORS[3], growSpeed: 7.5, retractSpeed: 8.0, delay: 0.02, progress: 0 },
    { x: 39, h: 25, w: 2.2, baseTilt: -0.06, swaySpeed: 2.6, swayAmp: 0.09, phase: 0.8, color: COLORS[2], growSpeed: 8.5, retractSpeed: 9.2, delay: 0.00, progress: 0 },
    { x: 46, h: 18, w: 1.8, baseTilt: -0.04, swaySpeed: 3.8, swayAmp: 0.13, phase: 3.1, color: COLORS[4], growSpeed: 7.0, retractSpeed: 8.5, delay: 0.03, progress: 0 },
    { x: 53, h: 14, w: 1.8, baseTilt:  0.05, swaySpeed: 3.4, swayAmp: 0.11, phase: 1.6, color: COLORS[1], growSpeed: 9.0, retractSpeed: 9.5, delay: 0.06, progress: 0 },

    // Middle cluster (gentle lawn peeking beneath the link text)
    { x: 68,  h: 10, w: 1.6, baseTilt: -0.05, swaySpeed: 3.0, swayAmp: 0.08, phase: 0.5, color: COLORS[0], growSpeed: 9.0, retractSpeed: 9.0, delay: 0.04, progress: 0 },
    { x: 82,  h: 12, w: 1.7, baseTilt:  0.06, swaySpeed: 2.7, swayAmp: 0.07, phase: 1.9, color: COLORS[3], growSpeed: 8.0, retractSpeed: 8.5, delay: 0.02, progress: 0 },
    { x: 96,  h:  9, w: 1.5, baseTilt: -0.04, swaySpeed: 3.6, swayAmp: 0.08, phase: 2.7, color: COLORS[1], growSpeed: 9.5, retractSpeed: 9.5, delay: 0.01, progress: 0 },
    { x: 110, h: 13, w: 1.8, baseTilt:  0.02, swaySpeed: 2.9, swayAmp: 0.07, phase: 0.3, color: COLORS[2], growSpeed: 8.5, retractSpeed: 9.0, delay: 0.00, progress: 0 },
    { x: 124, h: 10, w: 1.6, baseTilt: -0.05, swaySpeed: 3.3, swayAmp: 0.08, phase: 1.4, color: COLORS[4], growSpeed: 9.0, retractSpeed: 9.0, delay: 0.02, progress: 0 },
    { x: 138, h: 12, w: 1.7, baseTilt:  0.04, swaySpeed: 2.8, swayAmp: 0.07, phase: 2.2, color: COLORS[0], growSpeed: 8.0, retractSpeed: 8.5, delay: 0.03, progress: 0 },
    { x: 152, h: 10, w: 1.6, baseTilt: -0.03, swaySpeed: 3.4, swayAmp: 0.08, phase: 0.9, color: COLORS[3], growSpeed: 9.0, retractSpeed: 9.2, delay: 0.05, progress: 0 },

    // Right cluster (framing right of "Digital Garden")
    { x: 167, h: 15, w: 1.8, baseTilt: -0.05, swaySpeed: 3.5, swayAmp: 0.11, phase: 2.1, color: COLORS[1], growSpeed: 9.0, retractSpeed: 9.5, delay: 0.05, progress: 0 },
    { x: 174, h: 19, w: 1.9, baseTilt:  0.06, swaySpeed: 3.1, swayAmp: 0.12, phase: 0.7, color: COLORS[4], growSpeed: 7.5, retractSpeed: 8.5, delay: 0.03, progress: 0 },
    { x: 181, h: 26, w: 2.2, baseTilt:  0.10, swaySpeed: 2.6, swayAmp: 0.09, phase: 1.8, color: COLORS[2], growSpeed: 8.5, retractSpeed: 9.2, delay: 0.01, progress: 0 },
    { x: 188, h: 21, w: 2.0, baseTilt:  0.16, swaySpeed: 3.6, swayAmp: 0.14, phase: 3.0, color: COLORS[3], growSpeed: 7.5, retractSpeed: 8.0, delay: 0.02, progress: 0 },
    { x: 195, h: 28, w: 2.4, baseTilt:  0.22, swaySpeed: 2.9, swayAmp: 0.10, phase: 0.4, color: COLORS[1], growSpeed: 8.0, retractSpeed: 9.0, delay: 0.04, progress: 0 },
    { x: 202, h: 23, w: 2.2, baseTilt:  0.30, swaySpeed: 3.3, swayAmp: 0.13, phase: 1.5, color: COLORS[0], growSpeed: 7.0, retractSpeed: 8.5, delay: 0.08, progress: 0 },
  ];
  return blades;
}

interface GardenNavLinkProps {
  isEn?: boolean;
}

export default function GardenNavLink({ isEn = false }: GardenNavLinkProps) {
  const [hovered, setHovered] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const bladesRef = useRef<Blade[]>(createBlades());
  const rafRef = useRef<number | null>(null);
  const isRunningRef = useRef(false);
  const hoveredRef = useRef(false);
  const hoverTimeRef = useRef(0);
  const lastTimeRef = useRef(0);

  // Sync ref with state for animation loop without recreating RAF
  useEffect(() => {
    hoveredRef.current = hovered;
    if (hovered) {
      hoverTimeRef.current = 0;
      startLoop();
    }
  }, [hovered]);

  const startLoop = () => {
    if (isRunningRef.current) return;
    isRunningRef.current = true;
    lastTimeRef.current = performance.now();
    loop(performance.now());
  };

  const loop = (now: number) => {
    const canvas = canvasRef.current;
    if (!canvas) {
      isRunningRef.current = false;
      return;
    }

    const ctx = canvas.getContext("2d");
    if (!ctx) {
      isRunningRef.current = false;
      return;
    }

    const dt = Math.min((now - lastTimeRef.current) / 1000, 0.05); // cap delta
    lastTimeRef.current = now;

    const isHovered = hoveredRef.current;
    if (isHovered) {
      hoverTimeRef.current += dt;
    }

    const elapsed = hoverTimeRef.current;
    const time = now / 1000;
    const blades = bladesRef.current;
    let anyVisible = false;

    // Clear canvas
    ctx.clearRect(0, 0, CANVAS_W, CANVAS_H);

    // Update & draw blades
    for (let i = 0; i < blades.length; i++) {
      const b = blades[i];

      if (isHovered) {
        if (elapsed >= b.delay) {
          b.progress += (1 - b.progress) * Math.min(1, dt * b.growSpeed);
        }
      } else {
        b.progress += (0 - b.progress) * Math.min(1, dt * b.retractSpeed);
        if (b.progress < 0.005) {
          b.progress = 0;
        }
      }

      if (b.progress > 0) {
        anyVisible = true;
        const currentH = b.h * b.progress;
        if (currentH > 0.4) {
          const sway =
            Math.sin(time * b.swaySpeed + b.phase) * b.swayAmp * b.progress;
          const tilt = b.baseTilt + sway;

          ctx.save();
          ctx.translate(b.x, CANVAS_H);
          ctx.rotate(tilt);
          ctx.fillStyle = b.color;

          // Natural tapered grass blade with pointed tip
          const bw = b.w;
          ctx.beginPath();
          ctx.moveTo(-bw / 2, 0);
          ctx.quadraticCurveTo(-bw * 0.35, -currentH * 0.55, 0, -currentH);
          ctx.quadraticCurveTo(bw * 0.35, -currentH * 0.55, bw / 2, 0);
          ctx.closePath();
          ctx.fill();

          ctx.restore();
        }
      }
    }

    // Stop RAF when completely retracted and not hovered
    if (!isHovered && !anyVisible) {
      ctx.clearRect(0, 0, CANVAS_W, CANVAS_H);
      isRunningRef.current = false;
      rafRef.current = null;
      return;
    }

    rafRef.current = requestAnimationFrame(loop);
  };

  // Setup HiDPI canvas resolution
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const dpr = typeof window !== "undefined" ? window.devicePixelRatio || 1 : 1;
    canvas.width = CANVAS_W * dpr;
    canvas.height = CANVAS_H * dpr;

    const ctx = canvas.getContext("2d");
    if (ctx) {
      ctx.scale(dpr, dpr);
    }

    return () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, []);

  return (
    <div
      className="relative flex items-center justify-center"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onFocus={() => setHovered(true)}
      onBlur={() => setHovered(false)}
    >
      <Link
        href={isEn ? "/en/garden" : "/garden"}
        prefetch={false}
        className="relative z-10 text-sm text-black transition-colors hover:underline dark:text-white dark:hover:underline"
      >
        Digital Garden
      </Link>


      <canvas
        ref={canvasRef}
        aria-hidden="true"
        className="pointer-events-none absolute -bottom-1.5 left-1/2 -translate-x-1/2 overflow-visible"
        style={{
          width: `${CANVAS_W}px`,
          height: `${CANVAS_H}px`,
        }}
      />
    </div>
  );
}